import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { useCommandState } from "cmdk"
import {
	IconCubeOutline18 as Box,
	IconCheckOutline18 as Check,
	IconPlusOutline18 as PlusIcon,
} from "nucleo-ui-outline-18"

import type { buttonVariantsType, PopoverProps } from "@productlane/ui"
import type { Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Button, IconButton, Popover, Shortcut, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandAddNewItem,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { PopoverContentProps } from "@radix-ui/react-popover"
import type { Editor } from "@tiptap/react"
import { LinearTeamsDisabled } from "@/components/linear-teams-disabled"
import {
	propertyIconClassName,
	PropertyTrigger,
} from "@/components/property-trigger"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { hasLinearConnection } from "@/lib/linear-connection"
import { getProjectIcon } from "@/lib/projects"
import { useSelectedTeamIds } from "@/lib/use-team-filter"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { CreateProjectDialog } from "./create-project-dialog"

export interface ProjectSelectProps {
	projectIds: Array<string>
	onChange: (
		projectId: string | null,
		projectColor: string | null,
		linearId: string | null,
	) => void
	className?: string
	side?: PopoverContentProps["side"]
	align?: PopoverContentProps["align"]
	onCloseAutoFocus?: PopoverProps["onCloseAutoFocus"]
	modal?: PopoverProps["modal"]
	isHotkeyEnabled?: boolean
	isDisabled?: boolean
	editor?: Editor | null
	variant?: buttonVariantsType
	newProjectTitle?: string
	disableNewProject?: boolean
	selectedProject?: Project | null
	isPopoverPortal?: boolean
	// Renders the trigger as a chip in a detail page's Properties row.
	asProperty?: boolean
	teamId?: string | null
}

export const ProjectSelect = memo(
	({
		onChange,
		projectIds,
		className,
		side,
		align,
		modal,
		isHotkeyEnabled = true,
		onCloseAutoFocus,
		isDisabled,
		editor,
		variant = "outline",
		newProjectTitle,
		disableNewProject = false,
		selectedProject,
		isPopoverPortal = true,
		asProperty = false,
		teamId,
	}: ProjectSelectProps) => {
		const { role } = useRole()
		const workspace = useZeroWorkspace()
		const selectedTeamIds = useSelectedTeamIds()

		const [isPopoverOpen, setPopoverOpen] = useState(false)
		const [isDialogOpen, setDialogOpen] = useState(false)
		const [searchQuery, setSearchQuery] = useState("")
		const [highlighted, setHighlighted] = useState("")

		const [allProjects] = useQuery(
			queries.searchProjectsForProjectSelector({
				query: searchQuery,
			}),
		)

		const filteredProjects = useMemo(
			() =>
				allProjects.filter((p) =>
					teamId
						? p.linearTeamIds?.includes(teamId)
						: p.linearTeamIds?.some((id) => selectedTeamIds.includes(id)),
				),
			[allProjects, selectedTeamIds, teamId],
		)

		const selectedProjectIcon = useMemo(
			() => (selectedProject ? getProjectIcon(selectedProject.icon) : null),
			[selectedProject],
		)
		const SelectedProjectIcon = selectedProjectIcon

		const ref = useRef<HTMLButtonElement>(null)

		useShortcut(
			"meta+shift+p, ctrl+shift+p",
			() => {
				ref.current?.focus()
				setPopoverOpen(true)
			},
			{
				enabled: isHotkeyEnabled && !isDisabled && role !== "VIEWER",
				enableOnContentEditable: true,
			},
		)

		const openDialog = useCallback(() => {
			setDialogOpen(true)
			const selectedContent = editor
				? editor.state.doc.textBetween(
						editor.state.selection.from,
						editor.state.selection.to,
						" ",
					)
				: ""
			setHighlighted(selectedContent)
			setPopoverOpen(false)
		}, [editor])

		const onOpenChange = useCallback((open: boolean) => {
			setPopoverOpen(open)
			if (!open) {
				setSearchQuery("")
			}
		}, [])

		const selectProject = (project: Project | null) => {
			if (!project) {
				onChange(null, null, null)
			} else {
				onChange(project.id, project.color, project.linearProjectId)
			}

			setPopoverOpen(false)
			setDialogOpen(false)
			setSearchQuery("")
		}

		return (
			<>
				{!disableNewProject && (
					<CreateProjectDialog
						highlighted={highlighted}
						title={searchQuery || newProjectTitle || ""}
						isOpen={isDialogOpen}
						onOpenChange={(dialogOpen, newProject) => {
							if (!dialogOpen) {
								if (newProject) {
									onChange(
										newProject.id,
										newProject.color,
										newProject.linearProjectId,
									)
									setSearchQuery("")
									analytics.track("created-inline-project")
								}

								setDialogOpen(false)
								analytics.track("close-inline-create-project-dialog")
							}
						}}
					/>
				)}
				<Popover
					open={isPopoverOpen}
					onOpenChange={onOpenChange}
					modal={modal}
					onCloseAutoFocus={onCloseAutoFocus}
					isPortal={isPopoverPortal}
					content={
						<Command shouldFilter={false}>
							<div className="relative">
								<CommandInput
									autoFocus
									placeholder="Search projects..."
									value={searchQuery}
									onValueChange={setSearchQuery}
									className={cn("max-w-[18rem] md:max-w-[23rem]", {
										"max-w-[18rem] md:max-w-[23rem]": searchQuery,
									})}
									disabled={selectedTeamIds.length === 0}
								/>
								{!disableNewProject && (
									<div className="absolute right-2 top-2">
										<Tooltip label="Create Linear Project">
											<IconButton
												onClick={openDialog}
												Icon={PlusIcon}
												variant="ghost"
												className="text-secondary"
											/>
										</Tooltip>
									</div>
								)}
							</div>

							<CommandList>
								{selectedTeamIds.length === 0 && (
									<LinearTeamsDisabled
										onNavigate={() => {
											setPopoverOpen(false)
										}}
									/>
								)}
								{selectedProject && (
									<div className="p-1 pb-0">
										<CommandItem onSelect={() => selectProject(null)}>
											<div className="flex items-center gap-2 px-1 text-secondary">
												Clear selection
											</div>
										</CommandItem>
									</div>
								)}
								{filteredProjects.length > 0 && (
									<CommandGroup>
										{filteredProjects.map((project) => {
											const Icon = getProjectIcon(project.icon)
											return (
												<CommandItem
													key={project.id}
													value={project.id}
													onSelect={() => selectProject(project)}
												>
													<div className="flex items-center gap-2">
														<Icon
															className="h-3 w-3"
															style={{ color: project.color ?? "" }}
														/>
														<span className="flex-1 truncate">
															{project.name}
														</span>
														{projectIds.includes(project.id) && (
															<Check className="h-4 w-4" />
														)}
													</div>
												</CommandItem>
											)
										})}
									</CommandGroup>
								)}

								{!disableNewProject && (
									<AddNewItem
										onCreate={openDialog}
										filteredProjects={filteredProjects}
									/>
								)}
							</CommandList>
						</Command>
					}
					className="w-[90vw] max-w-md p-0"
					side={side}
					align={align}
					triggerTooltip={
						<div className="flex gap-1">
							<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
							<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
							<Shortcut>{ShortcutSymbols.P}</Shortcut>
						</div>
					}
				>
					{!hasLinearConnection(workspace) ? (
						<Tooltip label="Connect Linear to link threads to projects.">
							{/* need this empty div to make the tooltip working on disabled button */}
							<Link to="/settings/integrations/linear">
								<Button
									variant="outline"
									Icon={Box}
									className={cn("", className)}
									ref={ref}
									disabled
								>
									Project
								</Button>
							</Link>
						</Tooltip>
					) : asProperty ? (
						<PropertyTrigger
							className={cn(isDisabled && "pointer-events-none", className)}
							icon={
								selectedProject && SelectedProjectIcon ? (
									<SelectedProjectIcon
										className="h-4 w-4 flex-none"
										style={{ color: selectedProject.color ?? "" }}
									/>
								) : (
									<Box className={propertyIconClassName} />
								)
							}
						>
							{selectedProject?.name ?? "Project"}
						</PropertyTrigger>
					) : selectedProject && selectedProjectIcon ? (
						<Button
							variant={variant}
							Icon={selectedProjectIcon}
							iconStyle={{ color: selectedProject.color ?? "", flexShrink: 0 }}
							className={cn("truncate text-ellipsis", className)}
							ref={ref}
							disabled={isDisabled}
						>
							<p className="truncate">{selectedProject.name}</p>
						</Button>
					) : (
						<Button
							variant={variant}
							Icon={Box}
							className={cn(className)}
							ref={ref}
							disabled={isDisabled}
						>
							Project
						</Button>
					)}
				</Popover>
			</>
		)
	},
)

interface AddNewItemProps {
	onCreate: (value: string) => void
	filteredProjects: Project[]
}

const AddNewItem = memo(({ onCreate, filteredProjects }: AddNewItemProps) => {
	const search = useCommandState((state) => state.search).trim()
	const isVisible = useMemo(() => {
		if (!search) {
			return false
		}
		const exactMatch = filteredProjects.find(
			(o) => o.name.toLowerCase() === search.trim().toLowerCase(),
		)
		if (exactMatch) {
			return false
		}

		return true
	}, [filteredProjects, search])

	return (
		<CommandAddNewItem
			onCreate={onCreate}
			show={isVisible}
			search={search}
			onAnalyticsTrack={() => {
				analytics.track("open-inline-create-project-dialog")
			}}
		/>
	)
})
